import {
  createContext,
  useContext,
  useId,
  type HTMLAttributes,
  type LabelHTMLAttributes,
} from 'react';

import { cn } from '../lib/cn.js';
import { Label } from '../atoms/label.js';

/*
 * FormField is a composition primitive that wires a label, a control, and
 * optional help / error text together via context and ARIA attributes. It
 * generates a stable id so callers never have to manage it themselves.
 *
 * Usage:
 *   <FormField error="Name is required">
 *     <FormField.Label data-required>Studio name</FormField.Label>
 *     <FormField.Control>
 *       <Input />
 *     </FormField.Control>
 *     <FormField.ErrorText />
 *   </FormField>
 */

interface FormFieldContextValue {
  id: string;
  helpId: string;
  errorId: string;
  hasError: boolean;
  hasHelp: boolean;
  disabled: boolean;
}

const FormFieldContext = createContext<FormFieldContextValue | null>(null);

function useFormField(): FormFieldContextValue {
  const ctx = useContext(FormFieldContext);
  if (ctx === null) {
    throw new Error('useFormField must be used inside <FormField>');
  }
  return ctx;
}

/* ---- Root ----------------------------------------------------------------- */

export interface FormFieldProps extends HTMLAttributes<HTMLDivElement> {
  /** When set, ErrorText renders this message and the control gets aria-invalid. */
  // string | undefined explicit so callers under exactOptionalPropertyTypes can
  // pass state values typed `string | undefined` directly without coercion.
  error?: string | undefined;
  /** When set, HelpText renders this message. */
  help?: string | undefined;
  /** Propagates a disabled state to sub-components via context. */
  disabled?: boolean | undefined;
}

function FormFieldRoot({
  className,
  error,
  help,
  disabled = false,
  children,
  ...props
}: FormFieldProps) {
  const id = useId();
  const helpId = `${id}-help`;
  const errorId = `${id}-error`;

  return (
    <FormFieldContext.Provider
      value={{
        id,
        helpId,
        errorId,
        hasError: Boolean(error),
        hasHelp: Boolean(help),
        disabled,
      }}
    >
      <div
        className={cn('flex flex-col gap-1.5', className)}
        data-field-error={error ?? undefined}
        data-field-help={help ?? undefined}
        {...props}
      >
        {children}
      </div>
    </FormFieldContext.Provider>
  );
}

/* ---- Label ---------------------------------------------------------------- */

function FormFieldLabel({
  className,
  children,
  ...props
}: LabelHTMLAttributes<HTMLLabelElement>) {
  const { id, disabled } = useFormField();
  return (
    <Label
      htmlFor={id}
      className={className}
      data-disabled={disabled ? 'true' : undefined}
      {...props}
    >
      {children}
    </Label>
  );
}

/* ---- Control -------------------------------------------------------------- */

function FormFieldControl({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  const { id, errorId, helpId, hasError, hasHelp } = useFormField();

  const describedBy =
    [hasError ? errorId : null, hasHelp ? helpId : null]
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <div
      className={cn('relative', className)}
      /* Thread ARIA attributes into the first focusable child via data attrs;
         feature code uses the Input's own id / aria props for finer control.
         The div wrapper satisfies layout; real aria-* live on the control. */
      data-control-id={id}
      data-aria-describedby={describedBy}
      data-aria-invalid={hasError ? 'true' : undefined}
      {...props}
    >
      {children}
    </div>
  );
}

/* ---- HelpText ------------------------------------------------------------- */

function FormFieldHelpText({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLParagraphElement>) {
  const { helpId } = useFormField();
  return (
    <p
      id={helpId}
      className={cn('text-xs text-text-muted', className)}
      {...props}
    >
      {children}
    </p>
  );
}

/* ---- ErrorText ------------------------------------------------------------ */

function FormFieldErrorText({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLParagraphElement>) {
  const { errorId } = useFormField();
  return (
    <p
      id={errorId}
      role="alert"
      aria-live="polite"
      className={cn('text-xs text-danger-text', className)}
      {...props}
    >
      {children}
    </p>
  );
}

/* ---- Public API ----------------------------------------------------------- */

export const FormField = Object.assign(FormFieldRoot, {
  Label: FormFieldLabel,
  Control: FormFieldControl,
  HelpText: FormFieldHelpText,
  ErrorText: FormFieldErrorText,
});

export type { FormFieldContextValue };
